import { isFuture, isToday } from "date-fns"
import React from "react"
import TimelineItem from "@/components/timeline/timeline-item/timeline-item.tsx"
import GestationCalendarAccordion from "@/features/gestational-calendar/components/gestation-calendar-accordion/gestation-calendar-accordion.tsx"
import {
  CalendarActivityState,
  type GestationCalendar,
} from "@/features/gestational-calendar/types/calendar-types.ts"
import { getDayElapsedPercentageBetweenPastDateAndFutureDate } from "@/utils/helpers/time-utils.ts"

interface TimeLineTupleProps {
  gestationCalendar: GestationCalendar
  index: number
  lastIndex?: number
  isOpen?: boolean
  forcedActivityState?: CalendarActivityState
  showActionButtons?: boolean
}

const TimelineTuple: React.FC<TimeLineTupleProps> = ({
  gestationCalendar,
  index,
  lastIndex,
  isOpen,
  forcedActivityState,
  showActionButtons = true,
}) => {
  const currentActivity = gestationCalendar.entries[index]
  const nextActivity = gestationCalendar.entries[index + 1]
  const isCalvingDate =
    gestationCalendar.calving_date === currentActivity.event_date

  const abortionEvent =
    gestationCalendar.abortion_confirmed &&
    gestationCalendar.entries
      .filter((entry) => entry.animal_event)
      .sort((a, b) => {
        return (
          new Date(b.event_date).getTime() - new Date(a.event_date).getTime()
        )
      })[0]

  const isAbortedEvent =
    abortionEvent && abortionEvent.id === currentActivity.id

  const isAfterAbortion =
    abortionEvent && abortionEvent.event_date < currentActivity.event_date

  const dueDate = new Date(currentActivity.event_date)

  const completionPercentage =
    nextActivity &&
    getDayElapsedPercentageBetweenPastDateAndFutureDate(
      nextActivity?.event_date,
      currentActivity.event_date,
    )

  const activityState = (() => {
    if (isAbortedEvent) {
      return CalendarActivityState.ABORTED
    } else if (currentActivity.is_completed) {
      return CalendarActivityState.DONE
    } else if (isAfterAbortion) {
      return CalendarActivityState.DUE
    } else if (forcedActivityState) {
      return forcedActivityState
    } else if (isToday(dueDate)) {
      return CalendarActivityState.CURRENT
    } else if (isFuture(dueDate)) {
      return CalendarActivityState.DUE
    } else {
      return CalendarActivityState.DONE
    }
  })()

  const isAfterCalving =
    currentActivity.event.is_calving_relative &&
    gestationCalendar.confirmed_calving_date

  return (
    <TimelineItem
      isAfterAbortion={isAfterAbortion}
      activityState={activityState}
      completionPercentage={completionPercentage}
      isLast={index === lastIndex}
      isAfterCalving={!!isAfterCalving}
    >
      <GestationCalendarAccordion
        gestationCalendar={gestationCalendar}
        animalId={gestationCalendar.animal}
        activityState={activityState}
        calendarActivity={currentActivity}
        isCalvingDone={!!gestationCalendar.confirmed_calving_date}
        isCalving={isCalvingDate}
        isOpen={isOpen}
        isAfterCalving={!!isAfterCalving}
        showActionButtons={showActionButtons}
      />
    </TimelineItem>
  )
}

export default TimelineTuple
